import { c as _c } from "react/compiler-runtime";
import { Dispatch, SetStateAction, startTransition, useCallback, useEffect, useRef, useState } from 'react';
import { useClickAway, useDebounce, useKey } from 'react-use';
import useMediaQuery from '@/hooks/useMediaQuery';
import { getMediaQuery } from '@/styles/sizes';
import { useInAppDetect } from '@/hooks/useInAppDetect';
import { useIsLoggedIn } from '@/hooks/useIsLoggedIn';
import { useRouter } from 'next/router';
import { yieldToMain } from '@/utils/dom';
interface UseHeaderProps {
  headerRef: React.RefObject<HTMLDivElement | null>;
}
export type VariantProps = {
  windowWidth: number;
  isResponsive: boolean;
};
const CLOSE_SUBMENU_DELAY_MS = 150;
export const useHeader = t0 => {
  const $ = _c(49);
  const {
    headerRef
  } = t0;
  const [openedSubmenu, setOpenedSubmenu] = useState(null);
  const [debouncedOpenedSubmenu, setDebouncedOpenedSubmenu] = useState(null);
  let t1;
  if ($[0] !== openedSubmenu) {
    t1 = () => {
      startTransition(() => {
        setDebouncedOpenedSubmenu(openedSubmenu);
      });
    };
    $[0] = openedSubmenu;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== openedSubmenu) {
    t2 = [openedSubmenu];
    $[2] = openedSubmenu;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [, cancel] = useDebounce(t1, 100, t2);
  const [isOpenMobileMenu, setIsOpenMobileMenu] = useState(false);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = getMediaQuery("xl");
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const isResponsive = useMediaQuery(t3);
  const isInApp = useInAppDetect();
  const {
    isLoggedIn
  } = useIsLoggedIn();
  const [scrolling, setScrolling] = useState(false);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      y: 0
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const scrollPositionRef = useRef(t4);
  const lastScrollingStateRef = useRef(false);
  const openedSubmenuRef = useRef(null);
  const closeSubmenuTimeoutRef = useRef(null);
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      if (closeSubmenuTimeoutRef.current) {
        clearTimeout(closeSubmenuTimeoutRef.current);
        closeSubmenuTimeoutRef.current = null;
      }
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const clearCloseSubmenuTimeout = t5;
  let t6;
  if ($[7] !== cancel) {
    t6 = () => {
      clearCloseSubmenuTimeout();
      setOpenedSubmenu(null);
      setDebouncedOpenedSubmenu(null);
      cancel();
    };
    $[7] = cancel;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const closeSubmenuImmediately = t6;
  useClickAway(headerRef, closeSubmenuImmediately);
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setIsOpenMobileMenu(false);
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  useKey("esc", t7);
  let t8;
  let t9;
  if ($[10] !== openedSubmenu) {
    t8 = () => {
      openedSubmenuRef.current = openedSubmenu;
    };
    t9 = [openedSubmenu];
    $[10] = openedSubmenu;
    $[11] = t8;
    $[12] = t9;
  } else {
    t8 = $[11];
    t9 = $[12];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => clearCloseSubmenuTimeout;
    t11 = [clearCloseSubmenuTimeout];
    $[13] = t10;
    $[14] = t11;
  } else {
    t10 = $[13];
    t11 = $[14];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[15] !== closeSubmenuImmediately) {
    t12 = () => {
      let rafId = null;
      let lastScrollTime = 0;
      const handleScroll = () => {
        const now = Date.now();
        if (now - lastScrollTime < 100) {
          return;
        }
        lastScrollTime = now;
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
        rafId = requestAnimationFrame(() => {
          const isScrolled = window.scrollY > 10;
          if (lastScrollingStateRef.current !== isScrolled) {
            lastScrollingStateRef.current = isScrolled;
            startTransition(() => {
              setScrolling(isScrolled);
            });
          }
          if (openedSubmenuRef.current !== null) {
            closeSubmenuImmediately();
          }
        });
      };
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => {
        window.removeEventListener("scroll", handleScroll);
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
      };
    };
    t13 = [closeSubmenuImmediately];
    $[15] = closeSubmenuImmediately;
    $[16] = t12;
    $[17] = t13;
  } else {
    t12 = $[16];
    t13 = $[17];
  }
  useEffect(t12, t13);
  let t14;
  if ($[18] !== isOpenMobileMenu) {
    t14 = () => {
      if (isOpenMobileMenu) {
        document.body.style.overflow = "hidden";
        document.body.style.height = "100svh";
        scrollPositionRef.current = {
          y: window.scrollY
        };
      } else {
        document.documentElement.style.overflow = "";
        document.body.style.overflow = "";
        document.body.style.height = "";
        window.scrollTo({
          top: scrollPositionRef.current.y
        });
      }
    };
    $[18] = isOpenMobileMenu;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  const toggleLockScroll = t14;
  let t15;
  let t16;
  if ($[20] !== isOpenMobileMenu || $[21] !== toggleLockScroll) {
    t15 = () => {
      if (!isOpenMobileMenu) {
        toggleLockScroll();
      }
    };
    t16 = [isOpenMobileMenu, toggleLockScroll];
    $[20] = isOpenMobileMenu;
    $[21] = toggleLockScroll;
    $[22] = t15;
    $[23] = t16;
  } else {
    t15 = $[22];
    t16 = $[23];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[24] !== isOpenMobileMenu || $[25] !== isResponsive) {
    t17 = () => {
      if (isResponsive && isOpenMobileMenu) {
        setIsOpenMobileMenu(false);
      }
    };
    t18 = [isResponsive, isOpenMobileMenu];
    $[24] = isOpenMobileMenu;
    $[25] = isResponsive;
    $[26] = t17;
    $[27] = t18;
  } else {
    t17 = $[26];
    t18 = $[27];
  }
  useEffect(t17, t18);
  let t19;
  if ($[28] !== cancel || $[29] !== isResponsive) {
    t19 = function handleSubsectionOpen(sectionId, options) {
      yieldToMain(() => {
        if (isResponsive) {
          clearCloseSubmenuTimeout();
          if (options?.immediate) {
            setOpenedSubmenu(sectionId);
            setDebouncedOpenedSubmenu(sectionId);
            cancel();
            return;
          }
          setOpenedSubmenu(value => {
            if (value === sectionId) {
              return null;
            }
            return sectionId;
          });
        }
      });
    };
    $[28] = cancel;
    $[29] = isResponsive;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  const handleSubsectionOpen = t19;
  const router = useRouter();
  let t20;
  let t21;
  if ($[31] !== router.events) {
    t20 = () => {
      const handleRouteChange = () => {
        setIsOpenMobileMenu(false);
      };
      router.events.on("routeChangeComplete", handleRouteChange);
      return () => {
        router.events.off("routeChangeComplete", handleRouteChange);
      };
    };
    t21 = [router.events];
    $[31] = router.events;
    $[32] = t20;
    $[33] = t21;
  } else {
    t20 = $[32];
    t21 = $[33];
  }
  useEffect(t20, t21);
  let t22;
  if ($[34] !== closeSubmenuImmediately || $[35] !== isOpenMobileMenu || $[36] !== isResponsive) {
    t22 = function handleSubsectionClose() {
      requestAnimationFrame(() => {
        if (isResponsive && !isOpenMobileMenu) {
          clearCloseSubmenuTimeout();
          closeSubmenuTimeoutRef.current = setTimeout(() => {
            closeSubmenuImmediately();
          }, CLOSE_SUBMENU_DELAY_MS);
        }
      });
    };
    $[34] = closeSubmenuImmediately;
    $[35] = isOpenMobileMenu;
    $[36] = isResponsive;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  const handleSubsectionClose = t22;
  let t23;
  if ($[38] !== debouncedOpenedSubmenu || $[39] !== handleSubsectionClose || $[40] !== handleSubsectionOpen || $[41] !== isInApp || $[42] !== isLoggedIn || $[43] !== isOpenMobileMenu || $[44] !== isResponsive || $[45] !== openedSubmenu || $[46] !== scrolling || $[47] !== toggleLockScroll) {
    t23 = {
      openedSubmenu,
      debouncedOpenedSubmenu,
      isOpenMobileMenu,
      isResponsive,
      isInApp,
      isLoggedIn,
      scrolling,
      handleSubsectionOpen,
      handleSubsectionClose,
      setIsOpenMobileMenu,
      setOpenedSubmenu,
      setDebouncedOpenedSubmenu,
      toggleLockScroll
    };
    $[38] = debouncedOpenedSubmenu;
    $[39] = handleSubsectionClose;
    $[40] = handleSubsectionOpen;
    $[41] = isInApp;
    $[42] = isLoggedIn;
    $[43] = isOpenMobileMenu;
    $[44] = isResponsive;
    $[45] = openedSubmenu;
    $[46] = scrolling;
    $[47] = toggleLockScroll;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  return t23;
};